import {renameEvent} from 'common/report/util';
import {PROPERTIES} from 'common/report/constants';
import {combine, formatAlertDate, formatPercent, formatWholeNumber, keyword, percentDiff, pluralize} from './common';

const subContexts = {
  all: `ALL`,
  explains: `EXPLAINS`,
  forecast: `FORECAST`,
  summary: `SUMMARY`,
};

const validSubContext = (ctx) => Object.values(subContexts).includes(ctx);

function getAnomalyValue(anomalyDetail) {
  return anomalyDetail.valueAtTimestamp;
}

const insightsAnomaly = {
  explainsMessage: ({anomalyDetail}) => {
    const messageParts = [];
    messageParts.push(`We found specific values for `);

    anomalyDetail.explains.slice(0, 3).forEach((explain, index) => {
      if (index > 0 && index === anomalyDetail.explains.length - 1) {
        messageParts.push(`and `);
      }
      const property = explain.propertySegmentsDetail ? explain.propertySegmentsDetail.property : `User`;
      const formattedProperty = property in PROPERTIES ? PROPERTIES[property] : property;
      messageParts.push(keyword(formattedProperty));
      if (index < anomalyDetail.explains.length - 1) {
        if (anomalyDetail.explains.length > 2) {
          messageParts.push(`,`);
        }
        messageParts.push(` `);
      }
    });

    if (anomalyDetail.explains.length > 3) {
      messageParts.push(`and other properties`);
    }
    messageParts.push(` that may explain this anomaly.`);

    return messageParts;
  },

  forecastMessage: ({anomalyDetail, value}) => {
    const messageParts = [];
    const formattedValue = formatWholeNumber(value);
    messageParts.push(`The actual value is `, keyword(formattedValue));

    // TODO(mack): Even though the backend should be returning UNIQUE anomaly_detail before TOTAL, enforce this on
    // the front-end
    const {showAction} = anomalyDetail.insightsDetails;
    if (showAction === `UNIQUE`) {
      messageParts.push(` `, pluralize(`user`, value));
    }

    const {expectedValue} = anomalyDetail;
    const expectedDirection = value < expectedValue ? `lower` : `higher`;
    messageParts.push(
      `, which is `,
      keyword(expectedDirection),
      ` than our forecast of `,
      keyword(formatWholeNumber(expectedValue)),
      `.`,
    );

    return messageParts;
  },

  summaryMessage: ({anomalyDetail, displayContext, insightsQuery, utcOffset, value}) => {
    const eventName = keyword(renameEvent(insightsQuery.eventDisplayName));
    const {expectedValue} = anomalyDetail;
    const expectedDirection = value < expectedValue ? `lower` : `higher`;
    const messageParts = [eventName, ` was `];

    if (expectedValue !== 0) {
      const expectedDiff = percentDiff(value, expectedValue);
      messageParts.push(keyword(formatPercent(expectedDiff, 0)), ` `);
    }

    const timeUnit = insightsQuery.timeUnit;
    const anomalyDate = formatAlertDate(Number(anomalyDetail.anomalyTimestamp), {
      includeHours: timeUnit === `HOUR`,
      utcOffset,
    });

    messageParts.push(keyword(expectedDirection), ` than usual`);

    if (displayContext === `IN_REPORT`) {
      messageParts.push(` on `, anomalyDate);
    }

    messageParts.push(`.`);

    return messageParts;
  },
};

export function generateInsightsMessage(anomaly, {displayContext, subContext = `ALL`, typed, utcOffset = 0} = {}) {
  if (!validSubContext(subContext)) {
    throw `invalid subContext for insights alert message generation`;
  }

  // destructure for easy consumption by copy functions
  const {insightsQuery} = anomaly;
  const anomalyDetail = anomaly.anomalyDetail ? anomaly.anomalyDetail : anomaly.anomalyDetails[0];
  const value = getAnomalyValue(anomalyDetail);

  const {summaryMessage, forecastMessage, explainsMessage} = insightsAnomaly;

  const messageParts = {
    [subContexts.all]: (options) => [].concat(summaryMessage(options), [` `], forecastMessage(options)),
    [subContexts.summary]: summaryMessage,
    [subContexts.forecast]: forecastMessage,
    [subContexts.explains]: explainsMessage,
  }[subContext]({insightsQuery, utcOffset, displayContext, anomalyDetail, value});

  if (typed) {
    messageParts.unshift(keyword(`Insights Report: `));
  }

  return combine(messageParts);
}

const retentionAnomaly = {
  summaryMessage: ({value, anomalyDetail, utcOffset, retentionQuery}) => {
    const {retentionDetails} = anomalyDetail;
    const {interval} = retentionDetails;
    const retentionDate = formatAlertDate(Number(anomalyDetail.anomalyTimestamp) - interval * 86400000, {utcOffset});

    const formattedValue = (() => {
      if (retentionQuery.countType === `PERCENT`) {
        return formatPercent(value, 2);
      } else if (retentionQuery.countType === `COUNT`) {
        return formatWholeNumber(value);
      } else {
        console.error(`Unsupported countType ${retentionQuery.countType}`);
      }
    })();

    const emphasized = [`Day `, String(interval), ` retention was `, formattedValue].map(keyword);

    return [...emphasized, ` for users from `, retentionDate, `. `];
  },

  forecastMessage: ({value, anomalyDetail}) => {
    const expectedDirection = value < anomalyDetail.expectedValue ? `lower` : `higher`;
    const expectedValue = formatPercent(anomalyDetail.expectedValue, 2);
    const expectedDiff = formatPercent(percentDiff(value, anomalyDetail.expectedValue), 0);

    return [
      `This is `,
      keyword(expectedDiff),
      ` `,
      keyword(expectedDirection),
      ` than our forecast of `,
      expectedValue,
      `.`,
    ];
  },
};

function inReportRetentionMessage({anomaly, subContext = `ALL`, utcOffset}) {
  if (!validSubContext(subContext)) {
    throw `invalid subContext for insights alert message generation`;
  }

  const {anomalyDetail, retentionQuery} = anomaly;
  const value = getAnomalyValue(anomalyDetail);

  const {summaryMessage, forecastMessage} = retentionAnomaly;

  return combine(
    {
      [subContexts.all]: (options) => [].concat(summaryMessage(options), forecastMessage(options)),
      [subContexts.summary]: summaryMessage,
      [subContexts.forecast]: forecastMessage,
    }[subContext]({anomalyDetail, value, retentionQuery, utcOffset}),
  );
}

function globalRetentionMessage({anomaly, typed}) {
  const messageParts = [];

  const {anomalyDetails, retentionQuery} = anomaly;

  if (anomalyDetails.length > 1) {
    messageParts.push(`The retention rate `);
  } else {
    messageParts.push(`Day `, String(anomalyDetails[0].retentionDetails.interval), ` retention `);
  }

  if (retentionQuery.retentionType === `BIRTH`) {
    const bornEventName = keyword(renameEvent(retentionQuery.bornEventDisplayName));
    const eventName = keyword(renameEvent(retentionQuery.eventDisplayName));
    messageParts.push(`from `, bornEventName, ` -> `, eventName);
  } else if (retentionQuery.retentionType === `COMPOUNDED`) {
    const eventName = keyword(renameEvent(retentionQuery.eventDisplayName));
    messageParts.push(`from `, eventName, ` -> `, eventName);
  }

  if (anomalyDetails.length > 1) {
    messageParts.push(` for multiple time windows have changed.`);
  } else {
    const anomalyDetail = anomalyDetails[0];
    const value = getAnomalyValue(anomalyDetail);
    const expectedDiff = formatPercent(percentDiff(value, anomalyDetail.expectedValue), 0);
    const direction = anomalyDetail.direction === `NEGATIVE` ? `lower` : `higher`;
    messageParts.push(` is `, keyword(expectedDiff), ` `, direction, ` than expected.`);
  }

  if (typed) {
    messageParts.unshift(keyword(`Retention Report: `));
  }

  return combine(messageParts);
}

export function generateRetentionMessage(anomaly, {displayContext, subContext, typed, utcOffset = 0} = {}) {
  const messageFunc = displayContext === `IN_REPORT` ? inReportRetentionMessage : globalRetentionMessage;

  return messageFunc({anomaly, subContext, typed, utcOffset});
}

const funnelAnomaly = {
  summaryMessage: ({anomalyDate, anomalyDetail, funnelsDetails, funnelsQuery, value}) => {
    const messageParts = [];
    const formattedValue = formatPercent(value, 2);

    if (funnelsDetails.isOverallConversion) {
      messageParts.push(`The overall conversion rate `);
    } else {
      messageParts.push(`Conversion for this step `);
    }

    if (funnelsQuery.isPartial) {
      messageParts.push(`is currently `, formattedValue, `.`);
    } else {
      const direction = anomalyDetail.direction === `NEGATIVE` ? `decreased` : `increased`;
      messageParts.push(
        keyword(`has `),
        keyword(direction),
        keyword(` to `),
        keyword(formattedValue),
        ` for users who entered your funnel on `,
        anomalyDate,
        `.`,
      );
    }

    return messageParts;
  },

  forecastMessage: ({anomalyDate, anomalyDetail, funnelsQuery, value}) => {
    const expectedValue = formatPercent(anomalyDetail.expectedValue, 2);
    const expectedDirection = value < anomalyDetail.expectedValue ? `lower` : `higher`;
    const expectedDiff = formatPercent(percentDiff(value, anomalyDetail.expectedValue), 0);
    let optionalPartialMessage = [];

    if (funnelsQuery.isPartial) {
      optionalPartialMessage = [` for users who entered your funnel on `, anomalyDate];
    }

    return [
      `This is `,
      keyword(expectedDiff),
      ` `,
      keyword(expectedDirection),
      ` than our forecast of `,
      expectedValue,
      ...optionalPartialMessage,
      `.`,
    ];
  },
};

function inReportFunnelsMessage({anomaly, subContext = `ALL`, utcOffset}) {
  const {anomalyDetail, funnelsQuery} = anomaly;
  const {funnelsDetails} = anomalyDetail;
  const value = getAnomalyValue(anomalyDetail);
  const anomalyDate = formatAlertDate(Number(anomalyDetail.anomalyTimestamp), {utcOffset});

  const {summaryMessage, forecastMessage} = funnelAnomaly;

  return combine(
    {
      [subContexts.all]: (options) => [...summaryMessage(options), ` `, ...forecastMessage(options)],
      [subContexts.summary]: summaryMessage,
      [subContexts.forecast]: forecastMessage,
    }[subContext]({anomalyDate, anomalyDetail, funnelsDetails, funnelsQuery, value}),
  );
}

function globalFunnelsMessage({anomaly, typed}) {
  const messageParts = [];
  const {anomalyDetails, funnelsQuery} = anomaly;

  if (anomalyDetails.length > 1) {
    messageParts.push(`The conversion rates for multiple steps in `);
  } else {
    if (anomalyDetails[0].funnelsDetails.isOverallConversion) {
      messageParts.push(`The overall conversion rate for `);
    } else {
      messageParts.push(`Conversion for a step in `);
    }
  }

  messageParts.push(`your funnel, `, keyword(funnelsQuery.funnelName), `, `);

  if (anomalyDetails.length > 1) {
    messageParts.push(`have changed.`);
  } else {
    const anomalyDetail = anomalyDetails[0];
    const value = getAnomalyValue(anomalyDetail);
    const expectedDirection = value < anomalyDetail.expectedValue ? `lower` : `higher`;
    const expectedDiff = formatPercent(percentDiff(value, anomalyDetail.expectedValue), 0);
    const partialMsg = funnelsQuery.isPartial ? `currently ` : ``;
    messageParts.push(`is ${partialMsg}`, keyword(expectedDiff), ` `, expectedDirection, ` than expected.`);
  }

  if (typed) {
    messageParts.unshift(keyword(`Funnels Report: `));
  }

  return combine(messageParts);
}

export function generateFunnelsMessage(anomaly, {displayContext, subContext, typed, utcOffset = 0} = {}) {
  const messageFunc = displayContext === `IN_REPORT` ? inReportFunnelsMessage : globalFunnelsMessage;
  return messageFunc({anomaly, subContext, typed, utcOffset});
}
